import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

export interface ISignUpFormInputState {
  isPasswordVisible: boolean;
  isConfirmPasswordVisible: boolean;
  submitErrorMessage: string;
  isLoadingSubmitPending: boolean;
  isLoadingSubscribe: boolean;
  referralDetails: { referrer: string | null; name: string | null; rewards: string | null };

  uniqueKey: string;

  countrySearch: string;
  country: string;
  countryError: string;
  isCountryDropdownOpen: boolean;

  stateSearch: string;
  state: string;
  stateError: string;
  isStateDropdownOpen: boolean;

  firstName: string;
  firstNameError: string;

  lastName: string;
  lastNameError: string;

  email: string;
  emailError: string;

  password: string;
  passwordError: string;

  confirmPassword: string;
  confirmPasswordError: string;

  phone: string;
  phoneError: string;
  phoneVerificationCode: string;
  phoneVerificationCodeError: string;
  isLoadingPhoneNumber: boolean;
  phoneCountry: string;
  isPhoneCountryDropdownOpen: boolean;
}

export const initialState: ISignUpFormInputState = {
  isPasswordVisible: false,
  isConfirmPasswordVisible: false,
  submitErrorMessage: "",
  isLoadingSubmitPending: false,
  isLoadingSubscribe: false,
  referralDetails: {
    referrer: null,
    name: null,
    rewards: null,
  },

  uniqueKey: "",

  countrySearch: "",
  country: "",
  countryError: "",
  isCountryDropdownOpen: false,

  stateSearch: "",
  state: "",
  stateError: "",
  isStateDropdownOpen: false,

  firstName: "",
  firstNameError: "",

  lastName: "",
  lastNameError: "",

  email: "",
  emailError: "",

  password: "",
  passwordError: "",

  confirmPassword: "",
  confirmPasswordError: "",

  phone: "",
  phoneError: "",
  phoneVerificationCode: "",
  phoneVerificationCodeError: "",
  isLoadingPhoneNumber: false,
  phoneCountry: "",
  isPhoneCountryDropdownOpen: false,
};

const SignUpFormInputSlice = createSlice({
  name: "signUpFormInput",
  initialState: initialState,
  reducers: {
    setIsPasswordVisible: (state, action: PayloadAction<boolean>) => {
      state.isPasswordVisible = action.payload;
    },
    setIsConfirmPasswordVisible: (state, action: PayloadAction<boolean>) => {
      state.isConfirmPasswordVisible = action.payload;
    },
    setSubmitErrorMessage: (state, action: PayloadAction<string>) => {
      state.submitErrorMessage = action.payload;
    },
    setIsLoadingSubmitPending: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSubmitPending = action.payload;
    },
    setIsLoadingSubscribe: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSubscribe = action.payload;
    },
    setReferralDetails: (
      state,
      action: PayloadAction<{
        referrer: string | null;
        name: string | null;
        rewards: string | null;
      }>,
    ) => {
      state.referralDetails = action.payload;
    },

    // Unique key actions
    setUniqueKey: (state, action: PayloadAction<string>) => {
      state.uniqueKey = action.payload;
    },

    // Country actions
    setCountrySearch: (state, action: PayloadAction<string>) => {
      state.countrySearch = action.payload;
    },
    setCountry: (state, action: PayloadAction<string>) => {
      state.country = action.payload;
    },
    setCountryError: (state, action: PayloadAction<string>) => {
      state.countryError = action.payload;
    },
    setIsCountryDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.isCountryDropdownOpen = action.payload;
    },

    // State actions
    setStateSearch: (state, action: PayloadAction<string>) => {
      state.stateSearch = action.payload;
    },
    setState: (state, action: PayloadAction<string>) => {
      state.state = action.payload;
    },
    setStateError: (state, action: PayloadAction<string>) => {
      state.stateError = action.payload;
    },
    setIsStateDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.isStateDropdownOpen = action.payload;
    },

    // Name actions
    setFirstName: (state, action: PayloadAction<string>) => {
      state.firstName = action.payload;
    },
    setFirstNameError: (state, action: PayloadAction<string>) => {
      state.firstNameError = action.payload;
    },
    setLastName: (state, action: PayloadAction<string>) => {
      state.lastName = action.payload;
    },
    setLastNameError: (state, action: PayloadAction<string>) => {
      state.lastNameError = action.payload;
    },

    // Email actions
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setEmailError: (state, action: PayloadAction<string>) => {
      state.emailError = action.payload;
    },

    // Password actions
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    setPasswordError: (state, action: PayloadAction<string>) => {
      state.passwordError = action.payload;
    },
    setConfirmPassword: (state, action: PayloadAction<string>) => {
      state.confirmPassword = action.payload;
    },
    setConfirmPasswordError: (state, action: PayloadAction<string>) => {
      state.confirmPasswordError = action.payload;
    },

    // Phone actions
    setPhone: (state, action: PayloadAction<string>) => {
      state.phone = action.payload;
    },
    setPhoneError: (state, action: PayloadAction<string>) => {
      state.phoneError = action.payload;
    },
    setPhoneVerificationCode: (state, action: PayloadAction<string>) => {
      state.phoneVerificationCode = action.payload;
    },
    setPhoneVerificationCodeError: (state, action: PayloadAction<string>) => {
      state.phoneVerificationCodeError = action.payload;
    },
    setIsLoadingPhoneNumber: (state, action: PayloadAction<boolean>) => {
      state.isLoadingPhoneNumber = action.payload;
    },
    setPhoneCountry: (state, action: PayloadAction<string>) => {
      state.phoneCountry = action.payload;
    },
    setIsPhoneCountryDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.isPhoneCountryDropdownOpen = action.payload;
    },
  },
});

export const {
  setUniqueKey,
  setIsPasswordVisible,
  setIsConfirmPasswordVisible,
  setSubmitErrorMessage,
  setReferralDetails,
  setCountrySearch,
  setCountry,
  setCountryError,
  setIsCountryDropdownOpen,
  setStateSearch,
  setState,
  setStateError,
  setIsStateDropdownOpen,
  setFirstName,
  setFirstNameError,
  setLastName,
  setLastNameError,
  setEmail,
  setEmailError,
  setPassword,
  setPasswordError,
  setConfirmPassword,
  setConfirmPasswordError,
  setIsLoadingSubmitPending,
  setIsLoadingSubscribe,
  setPhone,
  setPhoneError,
  setPhoneVerificationCode,
  setPhoneVerificationCodeError,
  setPhoneCountry,
  setIsPhoneCountryDropdownOpen,
  setIsLoadingPhoneNumber,
} = SignUpFormInputSlice.actions;

// Use useSelector directly to avoid circular dependency with store.ts
export const useSignUpFormInputSlice = () =>
  useSelector((state: { signUpFormInput: ISignUpFormInputState }) => state.signUpFormInput);

export { SignUpFormInputSlice };
